import {Component, OnInit} from "@angular/core";
import {ContactService} from "./contact.service";
import {ContactModel, contactModelFactory, contactModelFormGroup} from "./contact.model";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import {FormGroup} from "@angular/forms";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../misc/handle-errors.decorator";

export interface ModifyContactDialogParamsInterface {
  targetType: string;
  targetLevel: string;
  targetId: string;
  contactService: ContactService;
}

export interface ModifyContactDialogResultInterface {
  changed: boolean;
  contact: ContactModel;
}

@Component({
  templateUrl: "./modify-contact-dialog.template.pug",
})
@exposeComponentInDebugMode()
export class ModifyContactDialogComponent
  extends DialogComponent<ModifyContactDialogParamsInterface, ModifyContactDialogResultInterface>
  implements ModifyContactDialogParamsInterface, OnInit {
  targetType: string;
  targetLevel: string;
  targetId: string;
  requiredFields = false;
  loading: boolean = false;
  foundContact: boolean = false;
  searchContactByEmail: string;

  contactService: ContactService;

  error: Error;
  contactForm: FormGroup = contactModelFormGroup();

  static showContactModal(
    dialogService: DialogService,
    model: ModifyContactDialogParamsInterface,
  ): Promise<ModifyContactDialogResultInterface> {
    return dialogService.showDialog(ModifyContactDialogComponent, model);
  }

  async submit() {
    this.loading = true;
    this.error = undefined;

    const params = {type: this.targetType, level: this.targetLevel, id: this.targetId};
    const contact = contactModelFactory(this.contactForm.value);

    try {
      const result = await this.contactService.updateContact(params, contact);

      this.result.changed = true;
      this.result.contact = contactModelFactory(result.contact);
      this.loading = false;
      this.clear();
      this.close();
    } catch (err) {
      this.loading = false;
      this.error = err;
      throw new Error(err.messages);
    }
  }

  async searchContact() {
    this.loading = true;
    this.error = undefined;

    const params = {
      type: this.targetType,
      level: this.targetLevel,
      id: this.targetId,
      emailAddress: this.searchContactByEmail,
    };

    try {
      const contact = await this.contactService.getITSMContactByEmail(params);

      if (contact.emailAddress) {
        this.result.contact = contact;
        this.contactForm.setValue(contact as {[p: string]: any});
        this.foundContact = true;
        this.loading = false;
      } else {
        this.foundContact = false;
        this.loading = false;
        throw new Error(`Contact not found with email ${this.searchContactByEmail}.`);
      }
    } catch (err) {
      this.loading = false;
      this.error = err;
      throw new Error(err.messages);
    }
  }

  @handleErrors()
  async clear() {
    this.loading = true;
    this.contactForm.setValue(contactModelFactory({}) as {[p: string]: any});
    this.foundContact = false;
    this.loading = false;
  }

  @handleErrors()
  async loadData() {
    const contact = await this.contactService.getContact({
      type: this.targetType,
      level: this.targetLevel,
      id: this.targetId,
    });

    this.result.contact = contact;
    this.contactForm.setValue(contact as {[p: string]: any});
  }

  ngOnInit() {
    switch (this.targetType) {
      case "installation":
      case "toner":
      case "breakfix":
      case "delivery":
        this.requiredFields = true;
        break;
      default:
        this.requiredFields = false;
        break;
    }

    this.loadData();
  }

  constructor(public dialogService: DialogService) {
    super(dialogService);

    this.result = {
      changed: false,
      contact: contactModelFactory(),
    };
  }
}
